// Presentation primitives shared by every benefit application card: the read-only field
// grid and the edit/save/cancel control set the editable sections all use.
import { useContext, useEffect, useMemo, useRef } from "react"
import { createPortal } from "react-dom"
import { IconCheck, IconEdit, IconLoader2, IconX } from "@tabler/icons-react"
import { BenefitPanelActions } from "./benefit-panel-actions"
import { InlineCardEditHost } from "./inline-card-edit-host"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { grantStatusLabels, proofingStatusLabels } from "@/lib/benefit-enums"
import type { BenefitWorkflowResponse } from "@/lib/benefit-types"
import { workflowBadge } from "@/lib/workflow-queue-display"

export function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm">{value}</dd>
    </div>
  )
}

export function FieldGrid({ children, cols = 4 }: { children: React.ReactNode; cols?: number }) {
  const colClass =
    cols === 2
      ? "grid-cols-1 sm:grid-cols-2"
      : cols === 3
        ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
        : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
  return <dl className={`grid ${colClass} gap-x-6 gap-y-3`}>{children}</dl>
}

export function EditableCardActions({
  isEditing,
  isSaving,
  canEdit,
  saveDisabled = false,
  restorePanelFocus = false,
  onEdit,
  onSave,
  onCancel,
}: {
  isEditing: boolean
  isSaving: boolean
  canEdit: boolean
  saveDisabled?: boolean
  restorePanelFocus?: boolean
  onEdit: () => void
  onSave: () => void
  onCancel: () => void
}) {
  const footer = useContext(BenefitPanelActions)
  const inlineHost = useContext(InlineCardEditHost)
  const editButton = useRef<HTMLButtonElement | null>(null)
  const focusAnchor = useRef<HTMLSpanElement | null>(null)
  // An in-place card presents these controls itself. Publish the existing handlers behind a
  // stable identity so the card re-renders on state changes, not on every handler recreation.
  const handlers = useRef({ onEdit, onSave, onCancel })
  handlers.current = { onEdit, onSave, onCancel }
  const published = useMemo(() => ({
    edit: () => handlers.current.onEdit(),
    save: () => handlers.current.onSave(),
    cancel: () => handlers.current.onCancel(),
  }), [])
  useEffect(() => {
    inlineHost?.({ isEditing, isSaving, canEdit, saveDisabled, ...published })
  }, [inlineHost, isEditing, isSaving, canEdit, saveDisabled, published])
  useEffect(() => () => { inlineHost?.(null) }, [inlineHost])
  const wasEditing = useRef(isEditing)
  useEffect(() => {
    const ended = !inlineHost && (footer || restorePanelFocus) && wasEditing.current && !isEditing
    wasEditing.current = isEditing
    if (!ended) return
    if (!restorePanelFocus) { editButton.current?.focus({ preventScroll: true }); return }
    // Let the modal's removed-control observer settle before restoring the stable target.
    // Cleanup prevents an unmounted form from stealing focus in another view.
    const frame = requestAnimationFrame(() => {
      const target = editButton.current ?? (footer ?? focusAnchor.current)?.closest('[role="dialog"]')?.querySelector<HTMLElement>('[data-slot="sheet-title"]')
      if (target?.isConnected) target.focus({ preventScroll: true })
    })
    return () => cancelAnimationFrame(frame)
  }, [footer, inlineHost, isEditing, restorePanelFocus])
  // The card owns presentation and focus while the editor is in place.
  if (inlineHost) return null
  if (!isEditing) {
    return <>{restorePanelFocus && <span hidden ref={focusAnchor} />}{canEdit ? (
      <Button ref={editButton} variant="outline" size="sm" onClick={onEdit}>
        <IconEdit className="size-4" />
        Edit
      </Button>
    ) : null}</>
  }

  const actions = <div className="flex items-center justify-end gap-2" role="group" aria-label="Editor actions">
    <Button variant={footer ? "outline" : "ghost"} size={footer ? "default" : "sm"} onClick={onCancel} disabled={isSaving}>
      {!footer && <IconX className="size-4" />}
      Cancel
    </Button>
    <Button size={footer ? "default" : "sm"} onClick={onSave} disabled={isSaving || saveDisabled}>
      {isSaving ? <IconLoader2 className="size-4 animate-spin" /> : !footer && <IconCheck className="size-4" />}
      Save
    </Button>
  </div>
  return <>{restorePanelFocus && <span hidden ref={focusAnchor} />}{footer ? <><span hidden data-benefit-editor-active />{createPortal(actions, footer)}</> : actions}</>
}

/** Current application status always uses server authority, never a compatibility byte. */
export function WorkflowStatusBadge({ workflow }: { workflow: BenefitWorkflowResponse | null }) {
  const badge = workflowBadge(workflow?.display, workflow?.legacyProofingStatus ?? 0)
  return <Badge variant={badge.variant}>{badge.label}</Badge>
}

/** Historical legacy status entries only; not a V2 current-state display. */
export function ProofingStatusBadge({ status }: { status: number }) {
  const text = proofingStatusLabels[status] ?? `Status ${status}`
  const variant =
    status === 5 || status === 30
      ? "default"
      : status === 100 || status === 120
        ? "destructive"
        : "secondary"
  return <Badge variant={variant}>{text}</Badge>
}

export function GrantStatusBadge({ status }: { status: number }) {
  const text = grantStatusLabels[status] ?? `Status ${status}`
  const variant =
    status === 30 || status === 60 || status === 70
      ? "default"
      : status === 80 || status === 81
        ? "destructive"
        : "secondary"
  return <Badge variant={variant}>{text}</Badge>
}

